import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationsContext";
import { fetchWorkforceAlerts } from "../../api/notifications";
import { isOwnerRole } from "../../utils/hrStaffHelpers";
import "./WorkforceLeadershipBanner.css";

function isLeadershipViewer(user) {
  if (!user) return false;
  if (user.role === "gm" || isOwnerRole(user.role)) return true;
  const roles = user.backendRoles || [];
  return roles.includes("gm") || roles.includes("owner");
}

function isUnread(notification) {
  return !(notification?.isRead ?? notification?.read);
}

export default function WorkforceLeadershipBanner() {
  const { user } = useAuth();
  const { notifications, markAsRead } = useNotifications();
  const [alerts, setAlerts] = useState([]);
  const visible = isLeadershipViewer(user);

  const loadAlerts = useCallback(async () => {
    if (!visible) return;
    try {
      const data = await fetchWorkforceAlerts();
      setAlerts(Array.isArray(data) ? data.filter(isUnread) : []);
    } catch {
      // Keep the last list if the refresh fails.
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      setAlerts([]);
      return undefined;
    }
    loadAlerts();
    const timer = setInterval(loadAlerts, 30_000);
    return () => clearInterval(timer);
  }, [visible, loadAlerts]);

  useEffect(() => {
    const readIds = new Set(
      notifications
        .filter((item) => !isUnread(item))
        .map((item) => String(item.id)),
    );
    if (readIds.size === 0) return;
    setAlerts((prev) => prev.filter((item) => !readIds.has(String(item.id))));
  }, [notifications]);

  if (!visible || alerts.length === 0) return null;

  const acknowledge = async (id) => {
    setAlerts((prev) => prev.filter((item) => String(item.id) !== String(id)));
    await markAsRead(id);
  };

  return (
    <section className="workforce-alert-banner" aria-live="polite">
      <div className="workforce-alert-heading">
        <strong>تنبيهات التعيين وإنهاء الخدمة</strong>
        <span>{alerts.length} غير مقروء</span>
      </div>
      <div className="workforce-alert-list">
        {alerts.map((alert) => {
          const ended = alert.type === "employee_terminated";
          return (
            <article
              key={alert.id}
              className={`workforce-alert-card ${ended ? "is-end" : "is-hire"}`}
            >
              <div className="workforce-alert-card-body">
                <span className="workforce-alert-badge">
                  {ended ? "إنهاء خدمة" : "تعيين جديد"}
                </span>
                <p>{alert.message}</p>
              </div>
              <button type="button" onClick={() => acknowledge(alert.id)}>
                تم الاطلاع
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}
